import dayjs, { type Dayjs } from 'dayjs';
import { Suspense, type ReactElement, useMemo } from 'react';
import {
  StageExecutionsTabContent,
  StageExecutionsToolbar,
  type StageExecutionSortKey,
} from '@/features/pipelines/components/StageExecutionsTab';
import { StagesTable, type StageSummaryRow } from '@/features/pipelines/components/StagesTable';
import { queryPipelineStages } from '@/features/pipelines/data';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { DeferredTablePanelSkeleton } from '@/features/shared/components/deferred/DeferredSectionSkeletons';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  tablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { useDateWindow, type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { Box, type GridColumnVisibilityModel, Tab, Tabs } from '@/ui/primitives';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';
import type { BuildStatus, QueryResult } from '@flowqa/shared';

export type StagesPanelTab = 'stages' | 'executions';

export type StageSortKey =
  | 'pipelineName'
  | 'stageName'
  | 'status'
  | 'executionTotal'
  | 'executionFrequencyPerDay'
  | 'executionAverageDurationMs'
  | 'executionPassRate';

const stageQuickSortOptions: Array<{
  key: StageSortKey;
  label: string;
  metric: 'passRate' | 'frequency' | 'avgDuration';
}> = [
  { key: 'executionPassRate', label: getChartTitle('passRate'), metric: 'passRate' },
  { key: 'executionFrequencyPerDay', label: getChartTitle('frequency'), metric: 'frequency' },
  { key: 'executionAverageDurationMs', label: getChartTitle('avgDuration'), metric: 'avgDuration' },
];

export type StagesTablePanelProps = {
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  tagOptions: TagOption[];
  activeTab: StagesPanelTab;
  onActiveTabChange: (nextTab: StagesPanelTab) => void;
  dateQuickFilter: DateQuickFilter;
  onDateQuickFilterChange: (next: DateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  latestQueryStartDay: string | null;
  latestQueryEndDay: string | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
  stageSearchValue: string;
  onStageSearchChange: (value: string) => void;
  stageColumnVisibilityModel: GridColumnVisibilityModel;
  onStageColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  stageSortKey: StageSortKey;
  stageSortDirection: 'asc' | 'desc';
  onStageSortChange: (nextSortKey: StageSortKey, nextSortDirection: 'asc' | 'desc') => void;
  stageFilterStatuses: BuildStatus[];
  stageFilterPipelineIds: string[];
  stageFilterStageNames: string[];
  onStageFilterStatusesChange: (next: BuildStatus[]) => void;
  stageFilterTags: string[];
  onStageFilterTagsChange: (next: string[]) => void;
  stageSummaryCount: number | null;
  stageExecutionSearchValue: string;
  onStageExecutionSearchChange: (value: string) => void;
  stageExecutionColumnVisibilityModel: GridColumnVisibilityModel;
  onStageExecutionColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  stageExecutionSortKey: StageExecutionSortKey;
  stageExecutionSortDirection: 'asc' | 'desc';
  onStageExecutionSortChange: (
    nextSortKey: StageExecutionSortKey,
    nextSortDirection: 'asc' | 'desc',
  ) => void;
  stageExecutionFilterStatuses: BuildStatus[];
  stageExecutionFilterPipelineIds: string[];
  stageExecutionFilterStageNames: string[];
  onStageExecutionFilterStatusesChange: (next: BuildStatus[]) => void;
  stageExecutionFilterTags: string[];
  onStageExecutionFilterTagsChange: (next: string[]) => void;
  stageExecutionSummaryCount: number | null;
};

export const StagesTablePanel = ({
  selectedProjectId,
  selectedOrganisationId,
  tagOptions,
  activeTab,
  onActiveTabChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  latestQueryStartDay,
  latestQueryEndDay,
  onFromDateChange,
  onToDateChange,
  stageSearchValue,
  onStageSearchChange,
  stageColumnVisibilityModel,
  onStageColumnVisibilityModelChange,
  stageSortKey,
  stageSortDirection,
  onStageSortChange,
  stageFilterStatuses,
  stageFilterPipelineIds,
  stageFilterStageNames,
  onStageFilterStatusesChange,
  stageFilterTags,
  onStageFilterTagsChange,
  stageSummaryCount,
  stageExecutionSearchValue,
  onStageExecutionSearchChange,
  stageExecutionColumnVisibilityModel,
  onStageExecutionColumnVisibilityModelChange,
  stageExecutionSortKey,
  stageExecutionSortDirection,
  onStageExecutionSortChange,
  stageExecutionFilterStatuses,
  stageExecutionFilterPipelineIds,
  stageExecutionFilterStageNames,
  onStageExecutionFilterStatusesChange,
  stageExecutionFilterTags,
  onStageExecutionFilterTagsChange,
  stageExecutionSummaryCount,
}: StagesTablePanelProps): ReactElement => {
  const availableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const dateWindow = useDateWindow({
    dateQuickFilter,
    fromDate,
    toDate,
    latestQueryStartDay,
    latestQueryEndDay,
  });
  const stageStatusFilterKey = useMemo(
    () => [...stageFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterStatuses],
  );
  const stageTagFilterKey = useMemo(
    () => [...stageFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterTags],
  );
  const stagePipelineFilterKey = useMemo(
    () => [...stageFilterPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterPipelineIds],
  );
  const stageNameFilterKey = useMemo(
    () => [...stageFilterStageNames].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterStageNames],
  );

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={activeTab}
                onChange={(_event, next) => {
                  onActiveTabChange(next as StagesPanelTab);
                }}
                aria-label="Stages table tabs"
                sx={tablePanelTabsSx}
              >
                <Tab label="Stages" value="stages" sx={tablePanelTabSx} />
                <Tab label="Runs" value="executions" sx={tablePanelTabSx} />
              </Tabs>
            </Box>
            {activeTab === 'stages' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<StageSortKey, BuildStatus>
                  title={undefined}
                  searchAriaLabel="Search stages"
                  searchValue={stageSearchValue}
                  onDebouncedSearchChange={onStageSearchChange}
                  columnVisibilityModel={stageColumnVisibilityModel}
                  defaultColumnVisibilityModel={stageColumnVisibilityModel}
                  columnOptions={[
                    { field: 'stageName', label: getDetailLabel('stage'), mandatory: true },
                    { field: 'status', label: getChartTitle('latestStatus') },
                    { field: 'latestJobStatusCounts', label: 'Latest Jobs' },
                    { field: 'executionStatusCounts', label: 'Runs' },
                    { field: 'metricsSummary', label: getDetailLabel('metrics') },
                  ]}
                  onColumnVisibilityModelChange={onStageColumnVisibilityModelChange}
                  sortKey={stageSortKey}
                  sortDirection={stageSortDirection}
                  defaultSortKey="stageName"
                  defaultSortDirection="asc"
                  sortOptions={[
                    { key: 'pipelineName', label: getDetailLabel('pipeline') },
                    { key: 'stageName', label: getDetailLabel('stage') },
                    { key: 'status', label: getDetailLabel('status') },
                    { key: 'executionTotal', label: 'Runs' },
                    { key: 'executionFrequencyPerDay', label: getChartTitle('frequency') },
                    { key: 'executionAverageDurationMs', label: getChartTitle('avgDuration') },
                    { key: 'executionPassRate', label: getChartTitle('passRate') },
                  ]}
                  quickSortOptions={stageQuickSortOptions}
                  onSortChange={onStageSortChange}
                  filterStatuses={stageFilterStatuses}
                  statusOptions={buildStatusOptions}
                  onFilterStatusesChange={onStageFilterStatusesChange}
                  filterTags={stageFilterTags}
                  onFilterTagsChange={onStageFilterTagsChange}
                  dateQuickFilter={dateQuickFilter}
                  onDateQuickFilterChange={onDateQuickFilterChange}
                  fromDate={fromDate}
                  toDate={toDate}
                  latestQueryStartDay={latestQueryStartDay}
                  latestQueryEndDay={latestQueryEndDay}
                  onFromDateChange={onFromDateChange}
                  onToDateChange={onToDateChange}
                  availableTags={availableTags}
                  summaryCount={stageSummaryCount}
                  summarySingularLabel="stage"
                  summaryPluralLabel="stages"
                  hideDateRangeLabel
                  showControlsWithoutSummary
                />
              </Box>
            ) : (
              <StageExecutionsToolbar
                searchValue={stageExecutionSearchValue}
                onSearchChange={onStageExecutionSearchChange}
                columnVisibilityModel={stageExecutionColumnVisibilityModel}
                onColumnVisibilityModelChange={onStageExecutionColumnVisibilityModelChange}
                sortKey={stageExecutionSortKey}
                sortDirection={stageExecutionSortDirection}
                onSortChange={onStageExecutionSortChange}
                filterStatuses={stageExecutionFilterStatuses}
                onFilterStatusesChange={onStageExecutionFilterStatusesChange}
                filterTags={stageExecutionFilterTags}
                onFilterTagsChange={onStageExecutionFilterTagsChange}
                dateQuickFilter={dateQuickFilter}
                onDateQuickFilterChange={onDateQuickFilterChange}
                fromDate={fromDate}
                toDate={toDate}
                latestQueryStartDay={latestQueryStartDay}
                latestQueryEndDay={latestQueryEndDay}
                onFromDateChange={onFromDateChange}
                onToDateChange={onToDateChange}
                availableTags={availableTags}
                summaryCount={stageExecutionSummaryCount}
                summarySingularLabel="run"
                summaryPluralLabel="runs"
                searchAriaLabel="Search stage runs"
              />
            )}
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      {activeTab === 'stages' ? (
        <Suspense
          fallback={<DeferredTablePanelSkeleton embedded titleWidth={96} variant="stages" />}
        >
          <StagesTable
            rows={[]}
            queryKey={queryKeys.dataGrid(
              'stages/table',
              selectedOrganisationId ?? null,
              selectedProjectId ?? null,
              dateQuickFilter,
              stageSearchValue,
              stageSortKey,
              stageSortDirection,
              stageStatusFilterKey,
              stagePipelineFilterKey,
              stageNameFilterKey,
              stageTagFilterKey,
              dateWindow.startDay,
              dateWindow.endDay,
            )}
            queryFn={async (input): Promise<QueryResult<StageSummaryRow>> =>
              await queryPipelineStages(selectedProjectId, selectedOrganisationId ?? undefined, {
                ...input,
                sorts:
                  input.sorts.length > 0
                    ? input.sorts
                    : ([
                        { field: stageSortKey, direction: stageSortDirection },
                      ] satisfies ApiSort[]),
                filters: [
                  ...input.filters,
                  ...mergeFilters(
                    buildContainsFilter('stageName', stageSearchValue),
                    buildInFilter('status', stageFilterStatuses),
                    buildInFilter('pipelineId', stageFilterPipelineIds),
                    buildInFilter('stageName', stageFilterStageNames),
                    buildInFilter('tags', stageFilterTags),
                    buildDateRangeFilters(
                      'startedAt',
                      dateWindow.startDay
                        ? dayjs(dateWindow.startDay).startOf('day').toISOString()
                        : null,
                      dateWindow.endDay
                        ? dayjs(dateWindow.endDay).endOf('day').toISOString()
                        : null,
                    ),
                  ),
                ],
              })
            }
            pageSizeOptions={[5, 10, 25, 50]}
            initialPageSize={5}
            preservePreviousData
            noRowsText={formatRangeWindowNoDataText(
              getEmptyStateMessage('entity.noneFound', { entity: 'stage' }),
              dateQuickFilter,
              dateWindow.hasCustomBounds,
            )}
            search={stageSearchValue}
            columnVisibilityModel={stageColumnVisibilityModel}
            onColumnVisibilityModelChange={onStageColumnVisibilityModelChange}
          />
        </Suspense>
      ) : (
        <StageExecutionsTabContent
          selectedProjectId={selectedProjectId}
          selectedOrganisationId={selectedOrganisationId}
          dateQuickFilter={dateQuickFilter}
          searchValue={stageExecutionSearchValue}
          sortKey={stageExecutionSortKey}
          sortDirection={stageExecutionSortDirection}
          filterStatuses={stageExecutionFilterStatuses}
          filterPipelineIds={stageExecutionFilterPipelineIds}
          filterStageNames={stageExecutionFilterStageNames}
          filterTags={stageExecutionFilterTags}
          startDay={dateWindow.startDay}
          endDay={dateWindow.endDay}
          hasCustomBounds={dateWindow.hasCustomBounds}
          columnVisibilityModel={stageExecutionColumnVisibilityModel}
          onColumnVisibilityModelChange={onStageExecutionColumnVisibilityModelChange}
        />
      )}
    </Panel>
  );
};
